import { useState, Fragment } from 'react'
import clsx from 'clsx'
import Lottie from 'lottie-react'
import LoaderAdm from '../../../assets/svgIcons/LoaderAdm.json'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { formatDate } from '../../../components/utils/formatter'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'

// Columnas ordenables
const orderableCols = [
  { col: 'Rut', field: 'rut' },
  { col: 'Proveedor', field: 'razonSocial' },
  { col: 'Fecha', field: 'fecha' },
  { col: 'Vencimiento', field: 'fechaVencimiento' },
  { col: 'Comprobante', field: 'comprobante' },
  { col: 'Tipo Comprobante', field: 'tipoComprobante' },
  { col: 'Folio Documento', field: 'folio' },
  { col: 'TIPO DOC/MOV', field: 'nombreFolio' },
  { col: 'Debe', field: 'debe' },
  { col: 'Haber', field: 'haber' },
  { col: 'Saldo', field: 'saldo' },
]

export default function Table({
  rows,
  className,
  lastElementRef,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [order, setOrder] = useState({
    col: 'folio',
    field: 'folio',
    direction: 'desc',
  })

  const handleSort = ({ col, field }) => {
    const isAsc = order.col === col && order.direction === 'asc'
    setOrder({ col, field, direction: isAsc ? 'desc' : 'asc' })
  }

  // Agrupa por RUT
  const groupRows = () => {
    const map = {}
    rows.forEach(row => {
      const key = row.rut
      const isNota = row.haber > 0 && row.debe === 0
      const tieneImporte =
        row.debe > 0 ||
        row.haber > 0 ||
        (Array.isArray(row.linkage) && row.linkage.length > 0) ||
        isNota

      if (tieneImporte) {
        if (!map[key]) map[key] = { razonSocial: key, entries: [] }
        map[key].entries.push(row)
      }
    })
    return Object.values(map)
  }

  const groupedRows = groupRows().sort((a, b) => {
    const cmp = (a.razonSocial || '').localeCompare(b.razonSocial || '')
    return order.direction === 'asc' ? cmp : -cmp
  })

  if (!groupedRows.length) {
    return <div className="py-12 text-center text-muted font-sans text-xs">No hay datos</div>
  }

  const formatCLP = v =>
    Number(v ?? 0).toLocaleString('es-CL', { minimumFractionDigits: 0 })

  if (isMobile) {
    const renderMetric = (label, value) => (
      <div className="rounded-lg bg-surface/70 border border-border px-2 py-2 min-w-0">
        <div className="text-[11px] text-muted">{label}</div>
        <div className="text-sm font-semibold text-text break-words">{value}</div>
      </div>
    )

    return (
      <div className={clsx('space-y-3 font-sans text-text', className)}>
        {groupedRows.map((group, gi) => {
          let totalDebe = 0
          let totalHaber = 0

          return (
            <Fragment key={gi}>
              {group.entries.map((row, ri) => {
                const tipoComp =
                  typeof row.tipoComprobante === 'string'
                    ? row.tipoComprobante
                    : row.tipoComprobante?.name || ''

                const isNota = Number(row.haber) > 0 && Number(row.debe) === 0
                const importe = isNota ? Number(row.haber) : Number(row.debe)

                if (isNota) totalHaber += importe
                else totalDebe += importe

                const pagos = Array.isArray(row.linkage)
                  ? [...row.linkage].sort(
                      (a, b) => new Date(a.fechaPago) - new Date(b.fechaPago)
                    )
                  : []

                let saldoAcum = importe
                return (
                  <Fragment key={`mob-${gi}-${ri}`}>
                    <div className="rounded-2xl border border-border bg-surface p-3">
                      <div className="text-xs font-semibold text-text">
                        {row.rut} {row.razonSocial}
                      </div>
                      <div className="mt-1 text-[11px] text-muted">
                        {row.fecha ? formatDate(row.fecha) : ''}{' '}
                        {row.fechaVencimiento
                          ? `· Vence ${formatDate(row.fechaVencimiento)}`
                          : ''}
                      </div>
                      <div className="mt-2 text-[11px] text-muted">
                        {tipoComp} · {row.numero ?? row.comprobante} ·{' '}
                        {row.numero ?? row.folio} · {row.nombreFolio}
                      </div>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {renderMetric('Debe', !isNota ? formatCLP(importe) : '')}
                        {renderMetric('Haber', isNota ? formatCLP(importe) : '')}
                        {renderMetric('Saldo', '')}
                      </div>
                    </div>

                    {pagos.map((link, li) => {
                      const mnt = Number(link?.montoRelacionado || 0)
                      totalDebe += mnt
                      saldoAcum -= mnt
                      return (
                        <div
                          key={`mob-${gi}-${ri}-${li}`}
                          className="rounded-2xl border border-border bg-surface/70 p-3"
                        >
                          <div className="text-xs font-semibold text-text">
                            {link?.nombreCuentaBanco || ''} · Pago
                          </div>
                          <div className="mt-1 text-[11px] text-muted">
                            {link?.fechaPago ? formatDate(link.fechaPago) : ''} ·{' '}
                            {link?.comprobante || ''}
                          </div>
                          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {renderMetric('Debe', formatCLP(mnt))}
                            {renderMetric('Saldo', formatCLP(Math.max(saldoAcum, 0)))}
                          </div>
                        </div>
                      )
                    })}
                  </Fragment>
                )
              })}

              <div className="rounded-2xl border border-border bg-surface/70 p-3">
                <div className="text-xs font-semibold text-text">
                  TOTAL {group.razonSocial}
                </div>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {renderMetric('Debe', formatCLP(totalDebe))}
                  {renderMetric('Haber', formatCLP(totalHaber))}
                  {renderMetric('Saldo', formatCLP(totalDebe - totalHaber))}
                </div>
              </div>
            </Fragment>
          )
        })}

        {hasNextPage && (
          <div ref={lastElementRef} className="flex justify-center py-4 flex-shrink-0">
            {isFetchingNextPage ? (
              <Lottie animationData={LoaderAdm} loop style={{ width: 100, height: 100 }} />
            ) : (
              <button
                onClick={onLoadMore}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Cargar más
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={`overflow-y-auto overflow-x-auto border border-border rounded bg-surface font-sans text-[10px] sm:text-[11px] text-text leading-5 ${className}`}>
      <table className="table-report-auto min-w-[1060px]" style={{ tableLayout: 'auto' }}>
        <thead className="sticky top-0 bg-surface">
          <tr>
            {orderableCols.map((colObj, idx) => {
              const isProveedor = colObj.col === 'Proveedor'
              const isComprobante = colObj.col === 'Comprobante'
              const isTipoComprobante = colObj.col === 'Tipo Comprobante'
              const isFolioDocumento = colObj.col === 'Folio Documento'
              const isTipoDocumento = colObj.field === 'nombreFolio'
              const allowWrap =
                isProveedor || isComprobante || isTipoComprobante || isFolioDocumento || isTipoDocumento
              const widthClass = isProveedor
                ? 'w-[150px]'
                : isComprobante
                  ? 'w-[105px]'
                  : isTipoComprobante
                    ? 'w-[105px]'
                    : isFolioDocumento
                      ? 'w-[108px]'
                      : isTipoDocumento
                        ? 'w-[108px]'
                        : ''

              return (
                <th
                  key={idx}
                  onClick={() => handleSort(colObj)}
                  className={`table-report-th align-middle text-center cursor-pointer select-none ${widthClass}`}
                >
                  <div className="flex w-full min-h-[34px] flex-col items-center justify-center leading-tight">
                    <span
                      className={clsx(
                        allowWrap
                          ? 'whitespace-normal break-words text-center'
                          : 'whitespace-nowrap text-center'
                      )}
                    >
                      {colObj.col}
                    </span>
                    <KeyboardArrowDownIcon
                      fontSize="small"
                      className={clsx('mt-0.5 transition-transform', {
                        'opacity-30': order.col !== colObj.field,
                        'rotate-180': order.col === colObj.field && order.direction === 'desc',
                      })}
                    />
                  </div>
                </th>
              )
            })}
          </tr>
        </thead>

        <tbody>
          {groupedRows.map((group, gi) => {
            let totalDebe = 0
            let totalHaber = 0

            return (
              <Fragment key={gi}>
                {group.entries.map((row, ri) => {
                  // Resuelve tipoComprobante: string o {id,name}
                  const tipoComp =
                    typeof row.tipoComprobante === 'string'
                      ? row.tipoComprobante
                      : row.tipoComprobante?.name || ''

                  const isNota = Number(row.haber) > 0 && Number(row.debe) === 0
                  const importe = isNota ? Number(row.haber) : Number(row.debe)

                  if (isNota) totalHaber += importe
                  else totalDebe += importe

                  // Fila principal
                  const facturaRow = (
                    <tr key={`fac-${gi}-${ri}`} className="table-report-row">
                      <td className="table-report-td text-center whitespace-nowrap">{row.rut}</td>
                      <td className="table-report-td text-center max-w-[150px] whitespace-normal break-words leading-tight">
                        {row.razonSocial}
                      </td>
                      <td className="table-report-td text-center whitespace-nowrap">
                        {row.fecha ? formatDate(row.fecha) : ''}
                      </td>
                      <td className="table-report-td text-center whitespace-nowrap">
                        {row.fechaVencimiento ? formatDate(row.fechaVencimiento) : ''}
                      </td>
                      <td className="table-report-td text-center max-w-[105px] whitespace-normal break-words leading-tight">
                        {row.numero ?? row.comprobante}
                      </td>
                      <td className="table-report-td text-center max-w-[105px] whitespace-normal break-words leading-tight">
                        {tipoComp}
                      </td>
                      <td className="table-report-td text-center max-w-[108px] whitespace-normal break-words leading-tight">
                        {row.numero ?? row.folio}
                      </td>
                      <td className="table-report-td text-center max-w-[108px] whitespace-normal break-words leading-tight">
                        {row.nombreFolio}
                      </td>
                      <td className="table-report-td text-right">
                        {!isNota ? formatCLP(importe) : ''}
                      </td>
                      <td className="table-report-td text-right">
                        {isNota ? formatCLP(importe) : ''}
                      </td>
                      <td className="table-report-td text-right"></td>
                    </tr>
                  )

                  // Filas de pagos (linkage)
                  const pagos = Array.isArray(row.linkage)
                    ? [...row.linkage].sort(
                        (a, b) => new Date(a.fechaPago) - new Date(b.fechaPago)
                      )
                    : []

                  let saldoAcum = importe
                  const pagosRows = pagos.map((link, li) => {
                    const mnt = Number(link?.montoRelacionado || 0)

                    // ✅ CORRECCIÓN: pagos de banco suman al DEBE (no al Haber)
                    totalDebe += mnt

                    // El saldo sigue disminuyendo con el pago
                    saldoAcum -= mnt

                    return (
                      <tr key={`pag-${gi}-${ri}-${li}`} className="table-report-row">
                        <td className="table-report-td text-center whitespace-nowrap">{row.rut}</td>
                        <td className="table-report-td text-center max-w-[150px] whitespace-normal break-words leading-tight">
                          {link?.nombreCuentaBanco || ''}
                        </td>
                        <td className="table-report-td text-center whitespace-nowrap">
                          {link?.fechaPago ? formatDate(link.fechaPago) : ''}
                        </td>
                        <td className="table-report-td text-center"></td>
                        <td className="table-report-td text-center max-w-[105px] whitespace-normal break-words leading-tight">{link?.comprobante}</td>
                        <td className="table-report-td text-center max-w-[105px] whitespace-normal break-words leading-tight">{tipoComp}</td>
                        <td className="table-report-td text-center max-w-[108px] whitespace-normal break-words leading-tight">{row.folio}</td>
                        <td className="table-report-td text-center max-w-[108px] whitespace-normal break-words leading-tight">Pago</td>

                        {/* ✅ Mostrar el pago en DEBE y dejar HABER vacío */}
                        <td className="table-report-td text-right">{formatCLP(mnt)}</td>
                        <td className="table-report-td text-right"></td>

                        <td className="table-report-td text-right">
                          {formatCLP(Math.max(saldoAcum, 0))}
                        </td>
                      </tr>
                    )
                  })

                  // Separador
                  const separador = (
                    <tr key={`sep-${gi}-${ri}`} className="h-0">
                      <td colSpan={orderableCols.length} className="border-b border-border p-0" />
                    </tr>
                  )

                  return [facturaRow, ...pagosRows, separador]
                })}

                {/* Totales por proveedor */}
                <tr className="table-report-total">
                  <td colSpan={8} className="table-report-td text-left">
                    TOTAL {group.razonSocial}
                  </td>
                  <td className="table-report-td text-right">{formatCLP(totalDebe)}</td>
                  <td className="table-report-td text-right">{formatCLP(totalHaber)}</td>
                  <td className="table-report-td text-right">{formatCLP(totalDebe - totalHaber)}</td>
                </tr>

                <tr>
                  <td colSpan={orderableCols.length} className="border-b border-border" />
                </tr>
              </Fragment>
            )
          })}
        </tbody>
      </table>

      {/* Infinite scroll trigger */}
      {hasNextPage && (
        <div ref={lastElementRef} className="flex justify-center py-4 flex-shrink-0">
          {isFetchingNextPage ? (
            <Lottie animationData={LoaderAdm} loop style={{ width: 100, height: 100 }} />
          ) : (
            <button
              onClick={onLoadMore}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Cargar más
            </button>
          )}
        </div>
      )}
    </div>
  )
}
